<script setup lang="ts">
import { ref } from 'vue'

const modalImage = ref<string | null>(null)

function openModal(imageSrc: string) {
  modalImage.value = imageSrc
}

function closeModal() {
  modalImage.value = null
}

import imgBpmGame from '@/assets/images/iphone 16 bpm game.png'
import imgSmartwatch from '@/assets/images/iphone 16 smartwatch connect.png'
import imgGuessGame from '@/assets/images/iphone 16 bpm guess game.png'
</script>

<template>
  <div class="template-content">
    <div class="container">
      <section>
        <h1 class="title">Over onze app</h1>
        <p class="description">
          Samen met ons team hebben we een interactieve app ontwikkeld voor de Hartstichting, speciaal voor evenementen.
          De app laat gebruikers op een leuke manier hun hartslag ervaren door muziek en spelletjes. Zo zien ze welk
          muzieknummer bij hun hartslag past en kunnen ze hun hartslag raden in een spel.<br> <br> Daarnaast is er een groepsspel
          waarbij deelnemers samen een gezamenlijke target hartslag proberen te halen, wat samenwerking en bewustzijn
          over hartgezondheid stimuleert. De app maakt hartslag meten speels, sociaal en inzichtelijk.
        </p>
      </section>
      <hr>
      <section>
        <article class="img-container">
          <img
              :src="imgBpmGame"
              alt="screenshot"
              class="thumbnail"
              @click="openModal(imgBpmGame)"
          />
          <h3 class="title">Verbind je hartslag met muziek en zie welk nummer bij jouw past</h3>
        </article>
      </section>
      <section>
        <article class="img-container">
          <h3 class="title">Eenvoud en fairness staat centraal</h3>
          <img
              :src="imgSmartwatch"
              alt="screenshot"
              class="thumbnail"
              @click="openModal(imgSmartwatch)"
          />
        </article>
      </section>
      <section>
        <article class="img-container">
          <img
              :src="imgGuessGame"
              alt="screenshot"
              class="thumbnail"
              @click="openModal(imgGuessGame)"
          />
          <h3 class="title">Gamification met je hartslag</h3>
        </article>
      </section>
      <div v-if="modalImage" class="modal" @click="closeModal">
        <img :src="modalImage" alt="modal screenshot" class="modal-content" />
      </div>
      <hr>
      <section>
        <h2 class="title">Aan de slag in 3 stappen...</h2>
        <article>
          <ol class="instructions">
            <li>
              <h3>App downloaden</h3>
              <p>
                Download de app via de <a class="p-link" href="#">Play Store</a> of de <a class="p-link" href="#">App Store</a>. Installeer de app op je smartphone en geef toestemming voor het gebruik van sensoren (zoals hartslagmeting, indien beschikbaar).
              </p>
            </li>
            <li>
              <h3>Check de evenementenagenda</h3>
              <p>
                Bekijk in de app of op de <a class="p-link" href="#">evenementenpagina</a> wanneer en waar wij aanwezig zijn. Tijdens sommige evenementen kun je samen met anderen de spellen spelen en je hartslagervaring delen.
              </p>
            </li>
            <li>
              <h3>Speel de spellen</h3>
              <p>
                In de app vind je drie interactieve spellen waarin je je hartslag op een unieke manier ervaart. Kies zelf welk spel je wilt spelen:
              </p>
              <ul class="games">
                <li>
                  <strong>Groepsspel:</strong><br>Maak een lobby aan en nodig anderen uit om samen te spelen. Het doel is om als groep gezamenlijk een specifieke doelhartslag te bereiken. Door rustiger of actiever te worden beïnvloed je je eigen hartslag en help je het team om het gezamenlijke doel te halen.
                </li>
                <li>
                  <strong>Ervaar je hartslag:</strong><br>In dit spel hoor je een muziekfragment dat past bij je hartslag op dat moment. Zo merk je meteen hoe je lichaam reageert, bijvoorbeeld na beweging of rust.
                </li>
                <li>
                  <strong>Raad je hartslag:</strong><br>In dit spel probeer je te raden wat je hartslag nu is. Daarna zie je het echte aantal en leer je beter naar je hart te luisteren.
                </li>
              </ul>
              <p>
                Tip: Speel de spellen meerdere keren op verschillende momenten van de dag of na verschillende activiteiten, en ervaar het verschil!
              </p>
            </li>
          </ol>
        </article>
      </section>
    </div>
  </div>
</template>

<style scoped>

hr {
  border: none;
  border-top: 1px solid var(--color-gray-light);
}

.p-link {
  color: var(--color-text-white-primary);
  text-decoration: underline;
}

.p-link:hover {
  cursor: pointer;
}

.games{
  list-style: none;
  padding: 1em;
  background-color: var(--color-accent-start);
  border-radius: var(--border-radius);
  color: var(--color-text-black);

  li {
    margin: 1em 0;
  }
}

.instructions {
  li::marker {
    font-size: 1rem;
    font-weight: bold;
  }
}

.modal {
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.85) !important;
}

.modal-content {
  max-width: 90%;
  max-height: 80%;
  border-radius: 12px;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
}

section {
  margin-bottom: 5em;
  margin-top: 3em;
}
.template-content {
  padding-top: 40px;
}

.container {
  margin: 0 20vw 0 20vw;
}

img {
  max-width: 35%;
  display: block;
  margin: 0 auto;
  border-radius: 15px;
  box-shadow: rgba(0, 0, 0, 0.24) 0 3px 8px;
}

.img-container {
  display: flex;

  h3 {
    margin-top: auto;
    margin-bottom: auto;
    width: 50%;
    text-align: center;
  }
}

@media screen and (max-width: 800px) {
  .container {
    margin: 0 8vw 0 8vw;
  }
  img {
    max-width: 45%;
  }
  h3 {
    font-size: 1rem;
  }
}

</style>